import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const SHORT_VIEWPORT_HEIGHT = 760;
const NARROW_VIEWPORT_WIDTH = 1024;

function formatRelatedPostDate(dateRaw) {
    const value = String(dateRaw || "").trim();

    if (!value) {
        return "";
    }

    const parsed = new Date(value.replace(" ", "T"));

    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return `${parsed.getMonth() + 1}/${parsed.getDate()}/${parsed.getFullYear()}`;
}

function useRelatedInsightsData() {
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [isShortViewport, setIsShortViewport] = useState(false);
    const [isNarrowViewport, setIsNarrowViewport] = useState(false);

    useEffect(() => {
        const syncRelatedPosts = (valueFromEvent) => {
            if (Array.isArray(valueFromEvent)) {
                setRelatedPosts(valueFromEvent);
                return;
            }

            try {
                const raw = localStorage.getItem("related_posts");
                const parsed = raw ? JSON.parse(raw) : [];

                setRelatedPosts(
                    Array.isArray(parsed) ? parsed : []
                );
            } catch (error) {
                setRelatedPosts([]);
            }
        };

        const handlePostsRelatedChange = (event) => {
            syncRelatedPosts(event?.detail);
        };

        syncRelatedPosts();

        window.addEventListener(
            "posts-related-change",
            handlePostsRelatedChange
        );

        return () => {
            window.removeEventListener(
                "posts-related-change",
                handlePostsRelatedChange
            );
        };
    }, []);

    useEffect(() => {
        const updateViewportFlags = () => {
            setIsShortViewport(
                window.innerHeight <= SHORT_VIEWPORT_HEIGHT
            );

            setIsNarrowViewport(
                window.innerWidth <= NARROW_VIEWPORT_WIDTH
            );
        };

        updateViewportFlags();

        window.addEventListener(
            "resize",
            updateViewportFlags
        );

        return () => {
            window.removeEventListener(
                "resize",
                updateViewportFlags
            );
        };
    }, []);

    return {
        relatedPosts,
        shouldMoveRelatedBelowPost:
            isShortViewport || isNarrowViewport,
    };
}

function CalendarIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            className="block h-3 w-3"
        >
            <path
                d="M7.5 2.75v2.5M16.5 2.75v2.5M3.75 8.75h16.5M6 4.75h12A2.25 2.25 0 0 1 20.25 7v11A2.25 2.25 0 0 1 18 20.25H6A2.25 2.25 0 0 1 3.75 18V7A2.25 2.25 0 0 1 6 4.75Z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function TagIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            className="block h-3 w-3"
        >
            <path
                d="M10.25 4.75H6.75A2.25 2.25 0 0 0 4.5 7v4.043a2.25 2.25 0 0 0 .659 1.591l5.707 5.707a2.25 2.25 0 0 0 3.182 0l4.293-4.293a2.25 2.25 0 0 0 0-3.182l-5.909-5.909a2.25 2.25 0 0 0-1.591-.659Z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            <circle
                cx="8.25"
                cy="8.25"
                r="1.1"
                fill="currentColor"
            />
        </svg>
    );
}

function MetaBadge({ icon, children }) {
    return (
        <div
            className="
                box-border
                inline-grid
                shrink-0
                grid-cols-[11px_auto]
                items-center
                gap-[4px]
                whitespace-nowrap
                rounded-full
                bg-slate-200/80
                px-[8px]
                py-[4px]
                font-sans
                font-normal
                leading-none
                text-slate-600
            "
            style={{
                border:
                    "1px solid rgba(148, 163, 184, 0.25)",
            }}
        >
            <span
                className="
                    grid
                    h-4
                    w-3
                    shrink-0
                    place-items-center
                    leading-none
                "
            >
                {icon}
            </span>

            <span
                className="
                    grid
                    h-4
                    shrink-0
                    place-items-center
                    whitespace-nowrap
                    leading-none
                "
            >
                <span
                    className="
                        capsize
                        block
                        whitespace-nowrap
                        font-sans
                        font-normal
                        leading-none
                    "
                    style={{
                        fontSize: "11px",
                        lineHeight: "11px",
                    }}
                >
                    {children}
                </span>
            </span>
        </div>
    );
}

export function RelatedInsights({
    relatedPosts = [],
    className = "",
}) {
    if (
        !Array.isArray(relatedPosts) ||
        relatedPosts.length === 0
    ) {
        return null;
    }

    return (
        <div
            className={`
                ${className}
                mb-4
                rounded-2xl
                border
                border-blue-100/80
                bg-blue-50/90
                px-3
                py-3
                text-slate-700
                shadow-sm
                backdrop-blur-md
                lg:mb-0
            `}
        >
            <div
                className="
                    mt-2
                    text-center
                    text-lg
                    font-semibold
                    text-slate-800
                "
            >
                Related Insights
            </div>

            <div className="mt-4 space-y-2">
                {relatedPosts.map((post) => {
                    const title = String(
                        post?.title || ""
                    ).trim();

                    const description = String(
                        post?.description || ""
                    ).trim();

                    const thumbnail = String(
                        post?.thumbnail_image || ""
                    ).trim();

                    const hashName =
                        String(
                            post?.tips_hash_name || ""
                        ).trim() || "Hust Media";

                    const dateLabel =
                        formatRelatedPostDate(
                            post?.createdate
                        );

                    return (
                        <Link
                            key={
                                post?.id ||
                                post?.uri ||
                                title
                            }
                            to={`/shop/posts/${post?.uri}`}
                            reloadDocument={true}
                            className="
                                block
                                rounded-xl
                                border
                                border-blue-100/80
                                bg-blue-200/60
                                p-2.5
                                no-underline
                                transition
                                hover:border-blue-300/90
                                hover:bg-blue-200/80
                            "
                        >
                            <div className="flex items-start gap-2.5">
                                {thumbnail ? (
                                    <img
                                        src={thumbnail}
                                        alt={
                                            title ||
                                            "related insight"
                                        }
                                        loading="lazy"
                                        className="
                                            h-14
                                            w-20
                                            flex-none
                                            rounded-lg
                                            border
                                            border-blue-100/80
                                            object-cover
                                        "
                                    />
                                ) : null}

                                <div className="min-w-0 flex-1">
                                    <div
                                        className="
                                            text-sm
                                            font-semibold
                                            leading-snug
                                            text-black
                                        "
                                    >
                                        {title}
                                    </div>

                                    <div
                                        className="
                                            mt-1
                                            text-xs
                                            leading-relaxed
                                            text-slate-500
                                        "
                                        style={{
                                            display:
                                                "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient:
                                                "vertical",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {description}
                                    </div>
                                </div>
                            </div>

                            <div
                                className="
                                    mt-2
                                    flex
                                    w-full
                                    flex-wrap
                                    items-center
                                    justify-start
                                    gap-2
                                "
                            >
                                {dateLabel ? (
                                    <MetaBadge
                                        icon={<CalendarIcon />}
                                    >
                                        {dateLabel}
                                    </MetaBadge>
                                ) : null}

                                <MetaBadge icon={<TagIcon />}>
                                    {hashName}
                                </MetaBadge>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

export function RelatedInsightsSlot({
    position,
    className = "",
}) {
    const {
        relatedPosts,
        shouldMoveRelatedBelowPost,
    } = useRelatedInsightsData();

    const shouldRender =
        position === "below"
            ? shouldMoveRelatedBelowPost
            : !shouldMoveRelatedBelowPost;

    if (!shouldRender) {
        return null;
    }

    return (
        <RelatedInsights
            relatedPosts={relatedPosts}
            className={className}
        />
    );
}